import CloseRounded from '@mui/icons-material/CloseRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { onSubscriptionLocked } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Wordmark } from '../components/Brand';
import { PoweredBy } from '../components/PoweredBy';
import { keys, useSubscriptionNotice } from '../features/shop/api';
import { noticeView, type SubscriptionNotice } from '../features/shop/lib';
import { ContactCard } from '../features/shop/subscription/ContactCard';
import { useSupportUnread } from '../features/support/api';
import { SupportDialog } from '../features/support/SupportDialog';
import { RedeemCard } from '../features/shop/subscription/SubscriptionPage';
import { IconTile } from '../features/shop/ui';
import { businessDate } from '../lib/format';
import { useNotify } from '../lib/notify';
import { isMenuOff } from './nav';
import { useShop } from '../shop/ShopProvider';

/**
 * Wraps the signed-in app: a locked or suspended shop sees only the locked screen; otherwise a
 * banner tells everyone about the trial, an ending subscription and the grace period.
 */
export function useSubscriptionGate(): { locked: boolean; notice: SubscriptionNotice | null } {
  const qc = useQueryClient();
  const { status } = useAuth();
  const q = useSubscriptionNotice(status === 'signedIn');
  // Any 402 SUBSCRIPTION_LOCKED means the shop just locked: re-read the status to switch over.
  useEffect(
    () =>
      onSubscriptionLocked(() => void qc.invalidateQueries({ queryKey: keys.subscriptionStatus })),
    [qc],
  );
  const notice = q.data ?? null;
  return { locked: Boolean(notice && (notice.locked || notice.suspended)), notice };
}

const DISMISS_KEY = 'sm.trialNotice';

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

/** The trial / ending / grace banner above every page. Only the plain trial notice can be hidden. */
export function SubscriptionBanner({ notice }: { notice: SubscriptionNotice }) {
  const { can, me } = useAuth();
  const view = noticeView(notice);
  // Hidden for this browser session, until the trial's day count changes.
  const stamp = `${notice.tenantId}:${String(notice.daysRemaining)}`;
  const [dismissed, setDismissed] = useState(readDismissed);
  if (!view || (view.severity === 'info' && dismissed === stamp)) return null;
  return (
    <Alert
      severity={view.severity}
      variant={view.severity === 'info' ? 'standard' : 'filled'}
      sx={{ mb: 2.5, alignItems: 'center' }}
      action={
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {can('subscription.view') && !isMenuOff(me, 'subscription') && (
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to="/subscription"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {view.severity === 'info' ? 'View plan' : 'Renew'}
            </Button>
          )}
          {view.severity === 'info' && (
            <IconButton
              color="inherit"
              size="small"
              aria-label="Hide for now"
              onClick={() => {
                setDismissed(stamp);
                try {
                  sessionStorage.setItem(DISMISS_KEY, stamp);
                } catch {
                  // Private mode: hidden until reload.
                }
              }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          )}
        </Stack>
      }
    >
      <AlertTitle sx={{ mb: 0.25, fontWeight: 700 }}>{view.title}</AlertTitle>
      {view.body}
    </Alert>
  );
}

/** Full-screen block for a locked or suspended shop: contacts, activation code and sign-out. */
export function LockedScreen({ notice }: { notice: SubscriptionNotice }) {
  const { me, can, logout } = useAuth();
  const { format } = useShop();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();
  const ended = businessDate(notice.paidThroughDate, format);
  const [chatOpen, setChatOpen] = useState(false);
  const { data: unread } = useSupportUnread();

  const title = notice.suspended
    ? 'Your shop is suspended'
    : notice.onTrial
      ? 'Your free trial has ended'
      : 'Your subscription has ended';
  const body = notice.suspended
    ? 'PackBytes has paused this shop. Contact us to resume it.'
    : `${notice.onTrial ? 'The trial' : 'Your paid period'} ended on ${ended} and the grace period is over, so the shop is locked. Renew to carry on exactly where you left off.`;

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: { xs: 3, sm: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 920 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
        >
          <Wordmark />
          <Button
            variant="outlined"
            size="small"
            startIcon={<LogoutRounded />}
            onClick={() => void logout().then(() => navigate('/login'))}
          >
            Sign out
          </Button>
        </Stack>
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <IconTile size={64} color="error">
                <LockRounded />
              </IconTile>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h1">
                  {title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 620 }}>
                  {body} Your data is safe and nothing has been deleted.
                </Typography>
                {me && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Signed in as <strong>{me.user.name}</strong> · {me.tenant.name}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Box
          sx={{
            mt: 2.5,
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              md: notice.suspended ? 'minmax(0,1fr)' : 'repeat(2, minmax(0,1fr))',
            },
            alignItems: 'start',
          }}
        >
          <Stack spacing={2.5}>
            <ContactCard />
            <Card>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h5">Prefer to write to us here?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Send your question straight to the PackBytes team without leaving PackPilot. Our
                  replies appear here.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ChatRounded />}
                  onClick={() => setChatOpen(true)}
                >
                  {unread ? `Send us a message (${String(unread)} new)` : 'Send us a message'}
                </Button>
              </CardContent>
            </Card>
          </Stack>
          {notice.suspended ? null : can('subscription.manage') ? (
            <RedeemCard
              locked
              onRedeemed={(r) => {
                // The status is already re-read (unlocking swaps this screen for the app); reload
                // everything that answered 402 while locked.
                notify.success(
                  `Shop unlocked: paid through ${businessDate(r.subscription?.paidThroughDate ?? r.paidThroughAfter.slice(0, 10), format)}`,
                );
                void qc.invalidateQueries();
              }}
            />
          ) : (
            <Card>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h5">Have an activation code?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Ask the shop owner (or someone who can manage the subscription) to sign in and
                  enter it.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
        <PoweredBy sx={{ mt: 4 }} />
      </Box>
      <SupportDialog open={chatOpen} onClose={() => setChatOpen(false)} />
    </Box>
  );
}
